import { FC } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { useResponsiveValue } from '#/hooks/useResponsiveValue';
import { CoreFooterFeature } from '#/service/config/features.types';
import { BreakpointKeys } from '#/service/constants';
import { remify } from '#/service/utils';

import { useFooter } from './Footer.hook';
import { FooterAccordion } from './FooterAccordion';
import { FooterSections } from './FooterSections';
import { Info } from './Info';

const FooterContainer = styled.footer`
  font: var(--fontStyleSubtitle);
  position: relative;
  background-color: var(--pageBackground);
  padding-top: ${remify(50)};
  display: block;

  a {
    color: var(--pageParagraphForeground);
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
`;

const disabledRoutes = ['playback', 'updatePassword', 'forgotPassword'];

export const Footer: FC = () => {
  const { Component } = useResponsiveValue(breakpointMap);
  const { enabled, sections, copyright, contact, socials } = useFooter();

  const location = useLocation();

  const paths = location.pathname?.split('/') || ['', ''];
  const isSettingsRoute = disabledRoutes.includes(paths[1]);

  if (!enabled || isSettingsRoute) {
    return null;
  }

  return (
    <FooterContainer>
      {sections.length ? <Component sections={sections} /> : null}
      <Info contact={contact} copyright={copyright} socials={socials} />
    </FooterContainer>
  );
};

export const FooterSectionsOrAccordion: FC<
  Pick<CoreFooterFeature, 'sections'>
> = ({ sections }) => {
  if (sections.length === 1) {
    return <FooterSections sections={sections} />;
  }

  return <FooterAccordion sections={sections} />;
};

const breakpointMap = {
  [BreakpointKeys.XS]: { Component: FooterSectionsOrAccordion },
  [BreakpointKeys.SM]: { Component: FooterSectionsOrAccordion },
  [BreakpointKeys.MD]: { Component: FooterSectionsOrAccordion },
  [BreakpointKeys.LG]: { Component: FooterSectionsOrAccordion },
  [BreakpointKeys.XL]: { Component: FooterSections },
  [BreakpointKeys.XXL]: { Component: FooterSections },
};
